'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const inputWrap = cva(
  [
    'flex items-center gap-2 rounded-control border bg-surface px-[var(--control-px)]',
    'transition-[border-color,box-shadow] duration-[var(--duration-fast)]',
    'focus-within:border-accent focus-within:shadow-[var(--ring)]',
    'has-[input:disabled]:bg-muted has-[input:disabled]:opacity-60',
  ],
  {
    variants: {
      size: {
        sm: 'h-[var(--control-height-sm)] text-sm',
        md: 'h-[var(--control-height)] text-base',
        lg: 'h-[var(--control-height-lg)] text-md',
      },
      invalid: { true: 'border-danger focus-within:border-danger', false: 'border-border-strong' },
    },
    defaultVariants: { size: 'md', invalid: false },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>,
    VariantProps<typeof inputWrap> {
  iconLeft?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  /** Class applied to the outer wrapper (the field itself keeps its own styling). */
  wrapperClassName?: string;
}

const affix = 'flex shrink-0 items-center text-fg-muted';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, wrapperClassName, size, invalid, iconLeft, prefix, suffix, disabled, ...rest },
  ref,
) {
  return (
    <div className={cn(inputWrap({ size, invalid }), wrapperClassName)}>
      {iconLeft ? <span className={affix}>{iconLeft}</span> : null}
      {prefix ? <span className={affix}>{prefix}</span> : null}
      <input
        ref={ref}
        disabled={disabled}
        className={cn(
          'min-w-0 flex-1 border-none bg-transparent font-sans text-fg outline-none',
          'placeholder:text-fg-muted disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      />
      {suffix ? <span className={affix}>{suffix}</span> : null}
    </div>
  );
});
